import { SkyDialog, SkyMessageBar, SkyTextarea } from "@skypointcloud/skypoint-ui";
import { useCallback, useState, type ReactElement } from "react";
import {
  DISABLE_REASON_MAX,
  type DataflowDetail,
  type DataflowDisabled,
} from "@shared/types/dataflows";
import { useDisableDataflow, useEnableDataflow } from "@/hooks/use-dataflows";
import { InlineErrorBar, useInlineError } from "@/lib/feedback/InlineErrorBar";
import { useNotice } from "@/lib/feedback/NoticeProvider";
import { formatInstant } from "@/lib/time/displayTime";
import { useDisplayTz } from "@/hooks/use-display-tz";

/**
 * Disable and Enable one connection (2026-09-28, owner: "Show the connector as disabled").
 *
 * One dialog for both, opened from the list's row menu and from the editor header. Disabling asks
 * for a reason (required, at most 200 characters): it is stored on the connection and on the
 * audit row beside the acting user. Enabling shows the reason it was disabled for and takes an
 * optional reason of its own. A refusal stays in the dialog (`InlineErrorBar`); a success closes
 * it, posts a notice and lets the caller take the server's answer.
 */

export type ConnectionToggleMode = "disable" | "enable";

export interface ConnectionToggleTarget {
  id: string;
  /** The name the list shows (display name, else name). */
  name: string;
  mode: ConnectionToggleMode;
  /** For Enable: why and since when it is disabled, shown so the reader knows what they undo. */
  disabled?: DataflowDisabled | null;
}

/** The notice after a change: disabled, enabled and ready, or enabled with setup to finish. */
function toggledNotice(target: ConnectionToggleTarget, detail: DataflowDetail): string {
  if (target.mode === "disable") {
    return `${target.name} disabled. Schedules skip it until it is enabled.`;
  }
  return detail.state === "ready"
    ? `${target.name} enabled. It imports again.`
    : `${target.name} enabled. Finish its setup to import it.`;
}

const REASON_REQUIRED = "Say why this connection is being disabled.";

const COPY: Record<
  ConnectionToggleMode,
  { title: (name: string) => string; description: string; action: string; keep: string }
> = {
  disable: {
    title: (name) => `Disable ${name}?`,
    description:
      "Import now is turned off and every schedule skips this connection until it is enabled. An import already running finishes. Nothing is deleted.",
    action: "Disable connection",
    keep: "Keep enabled",
  },
  enable: {
    title: (name) => `Enable ${name}?`,
    description:
      "It can be imported again, and the schedules that name it include it from their next run.",
    action: "Enable connection",
    keep: "Keep disabled",
  },
};

/**
 * The dialog and its opener. `onChanged` receives the server's answer (the editor takes it as its
 * latest write, so its stamp and its state move at once); the list needs nothing, because the
 * mutation reloads it.
 */
export function useConnectionToggle(onChanged?: (detail: DataflowDetail) => void): {
  open: (target: ConnectionToggleTarget) => void;
  dialog: ReactElement | null;
} {
  const [target, setTarget] = useState<ConnectionToggleTarget | null>(null);
  const open = useCallback((next: ConnectionToggleTarget) => setTarget(next), []);
  const dialog = target ? (
    <ConnectionToggleDialog
      // Remounted per target, so a reason typed for one connection never carries to the next.
      key={`${target.id}:${target.mode}`}
      target={target}
      onClose={() => setTarget(null)}
      onChanged={onChanged}
    />
  ) : null;
  return { open, dialog };
}

function ConnectionToggleDialog({
  target,
  onClose,
  onChanged,
}: {
  target: ConnectionToggleTarget;
  onClose: () => void;
  onChanged?: (detail: DataflowDetail) => void;
}) {
  const zone = useDisplayTz();
  const notice = useNotice();
  const failure = useInlineError();
  const disable = useDisableDataflow();
  const enable = useEnableDataflow();
  const [reason, setReason] = useState("");
  const [showRequired, setShowRequired] = useState(false);
  const copy = COPY[target.mode];
  const saving = disable.isPending || enable.isPending;
  const trimmed = reason.trim();
  const missing = target.mode === "disable" && trimmed.length === 0;

  const succeeded = (detail: DataflowDetail) => {
    onClose();
    // After the close, so the notice is not inside the modal's hidden window.
    notice.success(toggledNotice(target, detail));
    onChanged?.(detail);
  };

  const confirm = () => {
    if (saving) return;
    failure.clear();
    if (missing) {
      setShowRequired(true);
      return;
    }
    const handlers = {
      onSuccess: succeeded,
      onError: (error: Error) => failure.show(error, "That change was not saved."),
    };
    if (target.mode === "disable") disable.mutate({ id: target.id, reason: trimmed }, handlers);
    else enable.mutate({ id: target.id, ...(trimmed ? { reason: trimmed } : {}) }, handlers);
  };

  return (
    <SkyDialog
      open
      onOpenChange={(next) => {
        if (!next) onClose();
      }}
      // Closes on success only: a refusal is said inside, with the typing kept (ux-s10 §3).
      onBeforeClose={(why) => why !== "primaryAction"}
      size="small"
      testId="connection-disable-dialog"
      title={copy.title(target.name)}
      description={copy.description}
      primaryAction={{
        label: copy.action,
        destructive: target.mode === "disable",
        loading: saving,
        onClick: confirm,
      }}
      secondaryAction={{ label: copy.keep, onClick: onClose }}
    >
      <div className="flex flex-col gap-4">
        {target.mode === "enable" && target.disabled ? (
          <SkyMessageBar
            intent="info"
            layout="multiline"
            title={`Disabled ${formatInstant(target.disabled.at, zone, "short", { named: true })}`}
            message={target.disabled.reason}
            testId="connection-disabled-because"
          />
        ) : null}
        <SkyTextarea
          label={target.mode === "disable" ? "Reason" : "Reason (optional)"}
          required={target.mode === "disable"}
          value={reason}
          onChange={(event) => {
            setReason(event.target.value);
            if (showRequired) setShowRequired(false);
          }}
          hint={
            target.mode === "disable"
              ? "Recorded on the connection and in the audit trail beside your name."
              : "Recorded in the audit trail beside your name."
          }
          validationState={showRequired && missing ? "error" : "none"}
          validationMessage={showRequired && missing ? REASON_REQUIRED : undefined}
          maxLength={DISABLE_REASON_MAX}
          showCount
          rows={3}
          testId="disable-reason"
        />
        <InlineErrorBar
          error={failure.error}
          title={failure.error?.retryable ? undefined : "That change was not saved"}
          testId="connection-disable-error"
        />
      </div>
    </SkyDialog>
  );
}
